import React from 'react';

import { SHARED_FILES_ROOT_DROP, SHARED_FILES_NEW_GROUP_DROP } from './sharedFilesDrag';

const LONG_PRESS_MS = 450;
const MOVE_CANCEL_PX = 10;
const DROP_HIGHLIGHT_CLASS = 'shared-files-drop-highlight';

let ghostEl = null;
let activeSession = null;

function findDropTarget(x, y) {
  const el = document.elementFromPoint(x, y)?.closest('[data-shared-files-drop]');
  if (!el) return null;
  const messageId = el.dataset.sharedFilesDrop;
  if (!messageId) return null;
  const insertIndex = el.dataset.dropIndex != null ? Number(el.dataset.dropIndex) : null;
  const append = el.dataset.dropAppend === 'true';
  return { messageId, insertIndex, append };
}

function clearDropHighlights() {
  document.querySelectorAll(`[data-shared-files-drop].${DROP_HIGHLIGHT_CLASS}`).forEach((node) => {
    node.classList.remove(DROP_HIGHLIGHT_CLASS);
  });
}

function updateDropHighlight(x, y) {
  clearDropHighlights();
  const el = document.elementFromPoint(x, y)?.closest('[data-shared-files-drop]');
  if (el) {
    el.classList.add(DROP_HIGHLIGHT_CLASS);
  }
}

function removeGhost() {
  if (ghostEl?.parentNode) {
    ghostEl.parentNode.removeChild(ghostEl);
  }
  ghostEl = null;
}

function moveGhost(x, y) {
  if (!ghostEl) return;
  ghostEl.style.left = `${x}px`;
  ghostEl.style.top = `${y}px`;
}

function createGhost(x, y) {
  removeGhost();
  ghostEl = document.createElement('div');
  ghostEl.setAttribute('aria-hidden', 'true');
  ghostEl.className =
    'fixed z-[9999] w-14 h-14 -translate-x-1/2 -translate-y-1/2 rounded-lg border-2 border-primary-500 bg-primary-100/90 shadow-lg pointer-events-none flex items-center justify-center';
  ghostEl.innerHTML =
    '<svg class="w-5 h-5 text-primary-600" fill="currentColor" viewBox="0 0 20 20"><path d="M7 2a2 2 0 1 0 0 4 2 2 0 0 0 0-4zM7 8a2 2 0 1 0 0 4 2 2 0 0 0 0-4zM7 14a2 2 0 1 0 0 4 2 2 0 0 0 0-4zM13 2a2 2 0 1 0 0 4 2 2 0 0 0 0-4zM13 8a2 2 0 1 0 0 4 2 2 0 0 0 0-4zM13 14a2 2 0 1 0 0 4 2 2 0 0 0 0-4z"/></svg>';
  ghostEl.style.left = `${x}px`;
  ghostEl.style.top = `${y}px`;
  document.body.appendChild(ghostEl);
}

function onDocumentTouchMove(e) {
  if (!activeSession?.dragging) return;
  e.preventDefault();
  const touch = e.touches[0];
  if (!touch) return;
  moveGhost(touch.clientX, touch.clientY);
  updateDropHighlight(touch.clientX, touch.clientY);
  activeSession.onHighlight?.(findDropTarget(touch.clientX, touch.clientY));
}

function endSession(touch) {
  document.removeEventListener('touchmove', onDocumentTouchMove);
  document.removeEventListener('touchend', onDocumentTouchEnd);
  document.removeEventListener('touchcancel', onDocumentTouchEnd);

  const drop = touch ? findDropTarget(touch.clientX, touch.clientY) : null;
  if (drop && activeSession?.payload && activeSession?.onDrop) {
    activeSession.onDrop(activeSession.payload, drop);
  }

  clearDropHighlights();
  removeGhost();
  activeSession?.onHighlight?.(null);
  activeSession = null;
}

function onDocumentTouchEnd(e) {
  endSession(e.changedTouches[0]);
}

export function startTouchAttachmentDrag({ payload, touch, onDrop, onHighlight }) {
  if (!payload || !touch) return;

  if (activeSession) {
    endSession(null);
  }

  activeSession = {
    payload,
    onDrop,
    onHighlight,
    dragging: true,
  };

  if (navigator.vibrate) {
    navigator.vibrate(12);
  }

  createGhost(touch.clientX, touch.clientY);
  document.addEventListener('touchmove', onDocumentTouchMove, { passive: false });
  document.addEventListener('touchend', onDocumentTouchEnd);
  document.addEventListener('touchcancel', onDocumentTouchEnd);
}

export function useAttachmentTouchInteraction({ enabled, payload, onTap, onDrop, onHighlight }) {
  const timerRef = React.useRef(null);
  const draggingRef = React.useRef(false);
  const startRef = React.useRef(null);
  const payloadRef = React.useRef(payload);
  const onTapRef = React.useRef(onTap);
  const onDropRef = React.useRef(onDrop);
  const onHighlightRef = React.useRef(onHighlight);

  payloadRef.current = payload;
  onTapRef.current = onTap;
  onDropRef.current = onDrop;
  onHighlightRef.current = onHighlight;

  const cancelTimer = () => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  };

  const reset = () => {
    cancelTimer();
    draggingRef.current = false;
    startRef.current = null;
  };

  return {
    onTouchStart: (e) => {
      if (!enabled || e.touches.length !== 1) return;
      const touch = e.touches[0];
      startRef.current = { x: touch.clientX, y: touch.clientY };
      cancelTimer();
      timerRef.current = setTimeout(() => {
        draggingRef.current = true;
        startTouchAttachmentDrag({
          payload: payloadRef.current,
          touch,
          onDrop: onDropRef.current,
          onHighlight: onHighlightRef.current,
        });
      }, LONG_PRESS_MS);
    },
    onTouchMove: (e) => {
      if (!enabled || draggingRef.current) return;
      if (!startRef.current || e.touches.length !== 1) return;
      const touch = e.touches[0];
      const dx = touch.clientX - startRef.current.x;
      const dy = touch.clientY - startRef.current.y;
      if (Math.hypot(dx, dy) > MOVE_CANCEL_PX) {
        cancelTimer();
      }
    },
    onTouchEnd: (e) => {
      if (!enabled) return;
      if (draggingRef.current) {
        reset();
        return;
      }
      cancelTimer();
      if (startRef.current && e.changedTouches[0]) {
        const touch = e.changedTouches[0];
        const dx = touch.clientX - startRef.current.x;
        const dy = touch.clientY - startRef.current.y;
        if (Math.hypot(dx, dy) <= MOVE_CANCEL_PX) {
          onTapRef.current?.();
        }
      }
      reset();
    },
    onTouchCancel: () => {
      if (!enabled) return;
      reset();
    },
  };
}
